import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle } from "@phosphor-icons/react";

const inputCls =
  "w-full border border-slate-300 rounded-md px-3 py-2 text-sm text-slate-700 placeholder-slate-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none bg-white";

const GROUPS = [
  { key: "rs", label: "Rocketship", shipKey: "rsShip", procKey: "rsProc" },
  { key: "rcj", label: "RCJ", shipKey: "rcjShip", procKey: "rcjProc" },
];

export default function ProcessedCard({ day, closed, onSave }) {
  const [form, setForm] = useState({ rsShip: "", rsProc: "", rcjShip: "", rcjProc: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      rsShip: day?.rocketship_shipping || "",
      rsProc: day?.rocketship_processed != null ? String(day.rocketship_processed) : "",
      rcjShip: day?.rcj_shipping || "",
      rcjProc: day?.rcj_processed != null ? String(day.rcj_processed) : "",
    });
  }, [day?.id, day?.rocketship_shipping, day?.rocketship_processed, day?.rcj_shipping, day?.rcj_processed]);

  const save = async () => {
    setSaving(true);
    try {
      await onSave({
        rocketship_shipping: form.rsShip.trim(),
        rocketship_processed: parseInt(form.rsProc, 10) || 0,
        rcj_shipping: form.rcjShip.trim(),
        rcj_processed: parseInt(form.rcjProc, 10) || 0,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-sm p-4" data-testid="processed-card">
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">
        Total Packages Processed
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Doral enters the end-of-day totals for both locations.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {GROUPS.map((g) => (
          <div key={g.key} className="border border-slate-200 rounded-md p-3" data-testid={`processed-group-${g.key}`}>
            <div className="text-sm font-bold text-slate-900 mb-2">{g.label}</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-500">Shipping #</label>
                {closed ? (
                  <div className="text-sm font-semibold text-slate-900 mt-1" data-testid={`${g.key}-shipping-value`}>
                    {form[g.shipKey] || "—"}
                  </div>
                ) : (
                  <input
                    data-testid={`${g.key}-shipping-input`}
                    type="text"
                    value={form[g.shipKey]}
                    onChange={(e) => setForm({ ...form, [g.shipKey]: e.target.value })}
                    placeholder="e.g. RS-1024"
                    className={inputCls}
                  />
                )}
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500">Total packages processed</label>
                {closed ? (
                  <div className="text-sm font-semibold text-emerald-700 mt-1" data-testid={`${g.key}-processed-value`}>
                    {form[g.procKey] || "0"}
                  </div>
                ) : (
                  <input
                    data-testid={`${g.key}-processed-input`}
                    type="text"
                    inputMode="numeric"
                    value={form[g.procKey]}
                    onChange={(e) => setForm({ ...form, [g.procKey]: e.target.value })}
                    placeholder="0"
                    className={inputCls}
                  />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {!closed && (
        <div className="flex justify-end mt-4">
          <Button
            data-testid="processed-save-btn"
            onClick={save}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
          >
            <CheckCircle size={18} weight="bold" className="mr-2" />
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      )}
    </div>
  );
}

